import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { InlineSelect } from '../../InlineTable';
import { Badge, Button } from '../kit';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { linkedSystemFlags } from './orgModel';
import { DEFAULT_TIMEZONE } from './refreshModel';

/** Side panels sit at z 951; portal menus opened from them must be above. */
export const PANEL_MENU_Z = 1100;

const FALLBACK_TIMEZONES = [
  'America/Chicago', 'America/New_York', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver', 'America/Mexico_City',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Singapore', 'Australia/Sydney',
];

const buildTimezoneOptions = () => {
  let zones = FALLBACK_TIMEZONES;
  try {
    if (typeof Intl.supportedValuesOf === 'function') zones = Intl.supportedValuesOf('timeZone');
  } catch (_) {
    zones = FALLBACK_TIMEZONES;
  }
  return zones.map((zone) => ({ value: zone, label: zone.replace(/_/g, ' ') }));
};

export const TIMEZONE_OPTIONS = buildTimezoneOptions();

/** Searchable IANA timezone picker (InlineSelect in an input-shaped box). */
export function TimezoneSelect({ value, onChange, ariaLabel = 'Timezone', invalid, inline, disabled }) {
  const options = TIMEZONE_OPTIONS.some((o) => o.value === value) || !value
    ? TIMEZONE_OPTIONS
    : [{ value, label: value.replace(/_/g, ' ') }, ...TIMEZONE_OPTIONS];
  return (
    <div className={`orgs-selectbox${inline ? ' orgs-inline' : ''}`} aria-invalid={invalid ? 'true' : undefined}>
      <InlineSelect
        value={value || DEFAULT_TIMEZONE}
        options={options}
        onChange={(v) => onChange(v || DEFAULT_TIMEZONE)}
        rowActive
        clearable={false}
        disabled={disabled}
        placeholder="Select timezone…"
        minWidth={260}
        ariaLabel={ariaLabel}
        menuZIndex={PANEL_MENU_Z}
      />
    </div>
  );
}

/** Native currency select with the kit look. */
export function CurrencySelect({ id, value, onChange, options, includeDefault, defaultLabel = 'Default (USD)', disabled, ariaLabel }) {
  const known = options.some((o) => o.value === value);
  return (
    <select
      id={id}
      className="ak-select"
      value={value || ''}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    >
      {includeDefault ? <option value="">{defaultLabel}</option> : null}
      {!known && value ? <option value={value}>{value}</option> : null}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function LinkedBadges({ location }) {
  return (
    <div className="orgs-badges">
      {linkedSystemFlags(location).map((f) => (
        <Badge key={f.key} tone={f.linked ? 'ok' : 'off'} title={f.linked ? `${f.label}: ${f.value}` : `${f.label}: not linked`}>
          {f.badge}
          <span className="ak-sr">{f.linked ? ' linked' : ' not linked'}</span>
        </Badge>
      ))}
    </div>
  );
}

/** Plain (non-confirm) dialog using the kit's dialog chrome. */
export function OrgDialog({ open, title, onClose, children, footer, wide = true }) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      ref.current && ref.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="ak-layer ak-dialog-wrap" onClick={onClose}>
      <div
        className={`ak-dialog${wide ? ' ak-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Dialog'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ak-dialog-body">
          <h2>{title}</h2>
          {children}
        </div>
        <div className="ak-dialog-foot">
          {footer || <Button onClick={onClose}>Close</Button>}
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * "All N locations" / "3 of N locations" dropdown with checkboxes.
 *   value [] = all locations
 */
export function LocationPicker({ locations, value = [], onChange, disabled, loading }) {
  const selected = new Set(value.map(Number));
  const total = locations.length;
  let summary = loading ? 'Loading locations…' : `All ${total} location${total === 1 ? '' : 's'}`;
  if (value.length === 1) summary = locations.find((l) => Number(l.id) === Number(value[0]))?.name || '1 location';
  else if (value.length > 1) summary = `${value.length} of ${total} locations`;
  const toggle = (id) => {
    const n = Number(id);
    const next = selected.has(n) ? value.filter((v) => Number(v) !== n) : [...value, n];
    onChange(next.length === total ? [] : next);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="orgs-picker-trigger" disabled={disabled || loading || !total} aria-label={`Locations: ${summary}`}>
          <span>{summary}</span>
          <ChevronDown aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[240px]" style={{ zIndex: PANEL_MENU_Z }}>
        <DropdownMenuItem onSelect={() => onChange([])}>All locations</DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="ak-menu-list">
          {locations.map((l) => (
            <DropdownMenuPrimitive.CheckboxItem
              key={l.id}
              checked={selected.has(Number(l.id))}
              onSelect={(e) => { e.preventDefault(); toggle(l.id); }}
              className="relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none focus:bg-[var(--lodge-onyx-50)]"
            >
              <input type="checkbox" readOnly tabIndex={-1} className="ak-check" checked={selected.has(Number(l.id))} aria-hidden="true" />
              <span style={{ flex: 1 }}>{l.name || `Location ${l.id}`}</span>
              <span className="ak-muted" style={{ fontSize: 12 }}>{l.code}</span>
            </DropdownMenuPrimitive.CheckboxItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
